import { Plus, Trash2 } from 'lucide-react';
import type { CampaignExpense } from '../types/campaign';
import { formatEuro } from '../utils/currency';
import { useLanguage } from '../contexts/LanguageContext';
import { EXPENSE_SETTLED_ELSEWHERE } from '../utils/campaignExpenses';
import { RowList, StaticRow } from './ui/ListRows';
import { FormBlock } from './ui/FormBlock';

export interface PaidPayableOption {
  id: string;
  /** "Fatura #1 · 18/08/2026" */
  label: string;
  date?: string | null;
}

interface CampaignExpensesProps {
  expenses: CampaignExpense[];
  onChange: (expenses: CampaignExpense[]) => void;
  /**
   * Paid creator tranches an expense can be settled with. When given, each
   * expense shows whether it is still to settle or which payout settled it.
   */
  paidPayables?: PaidPayableOption[];
}

/**
 * Occasional deductions (e.g. "Débito NOS - Distribuição Publicidade") that
 * are subtracted from the next creator payout, and pass-through expenses
 * reimbursed to them. Each is marked as settled with the payout that took
 * care of it, so it is never withheld twice.
 * Collapsed to a single add button when there are none — this is a rare case.
 */
export function CampaignExpenses({ expenses, onChange, paidPayables }: CampaignExpensesProps) {
  const { t } = useLanguage();

  const addExpense = () => {
    onChange([
      ...expenses,
      { id: `temp-${crypto.randomUUID()}`, description: '', amount: 0, kind: 'deduction' },
    ]);
  };

  const updateExpense = (id: string, patch: Partial<CampaignExpense>) => {
    onChange(expenses.map(e => (e.id === id ? { ...e, ...patch } : e)));
  };

  const removeExpense = (id: string) => {
    onChange(expenses.filter(e => e.id !== id));
  };

  const settleExpense = (expense: CampaignExpense, value: string) => {
    if (!value) {
      updateExpense(expense.id, { settled_in: null, settled_date: null });
      return;
    }
    const payable = paidPayables?.find(p => p.id === value);
    updateExpense(expense.id, {
      settled_in: value,
      settled_date: payable?.date || expense.settled_date || new Date().toISOString().split('T')[0],
    });
  };

  const deductionTotal = expenses
    .filter(e => (e.kind ?? 'deduction') === 'deduction' && !e.settled_in)
    .reduce((sum, e) => sum + (e.amount || 0), 0);
  const settledDeductionTotal = expenses
    .filter(e => (e.kind ?? 'deduction') === 'deduction' && !!e.settled_in)
    .reduce((sum, e) => sum + (e.amount || 0), 0);
  const passthroughTotal = expenses
    .filter(e => e.kind === 'passthrough')
    .reduce((sum, e) => sum + (e.amount || 0), 0);

  if (expenses.length === 0) {
    return (
      <div className="flex justify-end">
        <button
          type="button"
          onClick={addExpense}
          className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-gray-800 transition-colors"
        >
          <Plus size={14} />
          {t('expenses.addButton')}
        </button>
      </div>
    );
  }

  return (
    <FormBlock title={t('expenses.title')}>
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-gray-500 min-w-0 flex-1 basis-64">{t('expenses.subtitle')}</p>
        <button
          type="button"
          onClick={addExpense}
          className="cm-btn-add whitespace-nowrap shrink-0"
        >
          <Plus size={14} />
          {t('expenses.addButton')}
        </button>
      </div>

      <RowList>
        {expenses.map((expense) => (
          <StaticRow key={expense.id} tone={expense.settled_in ? 'success' : 'warning'}>
          <div className={`flex flex-wrap items-center gap-2 ${expense.settled_in ? 'opacity-80' : ''}`}>
            <select
              value={expense.kind ?? 'deduction'}
              onChange={(e) => updateExpense(expense.id, { kind: e.target.value as CampaignExpense['kind'] })}
              className="cm-select !h-9 w-full sm:!w-52 text-xs"
              title={(expense.kind ?? 'deduction') === 'deduction'
                ? t('expenses.kind.deductionHint')
                : t('expenses.kind.passthroughHint')}
            >
              <option value="deduction">{t('expenses.kind.deduction')}</option>
              <option value="passthrough">{t('expenses.kind.passthrough')}</option>
            </select>
            <input
              type="text"
              value={expense.description}
              onChange={(e) => updateExpense(expense.id, { description: e.target.value })}
              placeholder={t('expenses.descriptionPlaceholder')}
              className="cm-input !h-9 flex-1 min-w-[12rem] text-sm"
            />
            {paidPayables && (() => {
              const isDeduction = (expense.kind ?? 'deduction') === 'deduction';
              const known = !expense.settled_in
                || expense.settled_in === EXPENSE_SETTLED_ELSEWHERE
                || paidPayables.some(p => p.id === expense.settled_in);
              return (
                <select
                  value={expense.settled_in || ''}
                  onChange={(e) => settleExpense(expense, e.target.value)}
                  className={`cm-select !h-9 w-full sm:!w-56 text-xs ${expense.settled_in ? 'text-emerald-700' : 'text-amber-700'}`}
                  title={t(isDeduction ? 'expenses.settle.deductionHint' : 'expenses.settle.passthroughHint')}
                >
                  <option value="">{t(isDeduction ? 'expenses.settle.pendingDeduction' : 'expenses.settle.pendingPassthrough')}</option>
                  {paidPayables.map(p => (
                    <option key={p.id} value={p.id}>
                      {t(isDeduction ? 'expenses.settle.deductedIn' : 'expenses.settle.paidIn').replace('{0}', p.label)}
                    </option>
                  ))}
                  {!known && (
                    <option value={expense.settled_in as string}>{t('expenses.settle.settled')}</option>
                  )}
                  <option value={EXPENSE_SETTLED_ELSEWHERE}>{t('expenses.settle.elsewhere')}</option>
                </select>
              );
            })()}
            <input
              type="number"
              step="0.01"
              min="0"
              value={expense.amount || ''}
              onChange={(e) => updateExpense(expense.id, { amount: parseFloat(e.target.value) || 0 })}
              placeholder="0,00"
              className="cm-input !h-9 w-32 text-sm text-right"
            />
            <button
              type="button"
              onClick={() => removeExpense(expense.id)}
              className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              title={t('expenses.remove')}
            >
              <Trash2 size={16} />
            </button>
          </div>
          </StaticRow>
        ))}
      </RowList>

      <div className="flex flex-wrap justify-end gap-4 text-xs text-gray-600 border-t border-gray-100 pt-2">
        {passthroughTotal > 0 && (
          <span>
            {t('expenses.passthroughTotal')}:{' '}
            <span className="font-semibold text-amber-700 tabular-nums">{formatEuro(passthroughTotal)}</span>
          </span>
        )}
        {settledDeductionTotal > 0 && (
          <span>
            {t('expenses.settledTotal')}:{' '}
            <span className="font-semibold text-emerald-700 tabular-nums">{formatEuro(settledDeductionTotal)}</span>
          </span>
        )}
        {deductionTotal > 0 && (
          <span>
            {t('expenses.total')}:{' '}
            <span className="font-semibold text-red-700 tabular-nums">-{formatEuro(deductionTotal)}</span>
          </span>
        )}
      </div>
    </div>
    </FormBlock>
  );
}
